import { Check, ChevronRight, Settings2, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Dropdown } from '@/app/components/ui/Dropdown';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { useSession } from '@/features/auth/useSession';
import { cn } from '@/lib/cn';

/** Initials from a display name (or e-mail as a fallback) — "wo" from
 * "wouter@…" has no space to split on, so a single-word source keeps its
 * first two letters instead of collapsing to one. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  }
  return (parts[0] ?? '?').slice(0, 2).toUpperCase();
}

function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  return (
    <span
      className={cn(
        'bg-mint text-mint-ink rounded-pill flex shrink-0 items-center justify-center font-semibold',
        size === 'md' ? 'size-9 text-sm' : 'size-7 text-[11px]',
      )}
    >
      {initials(name)}
    </span>
  );
}

function MenuDivider() {
  return <hr className="border-line my-3" />;
}

/**
 * The account menu (§ Accountmenu): who you are, other known accounts, the
 * active company (if there is a choice), Manage and sign-out. Roles,
 * language, theme and the subscription moved to their own tabs under the
 * Profiel left menu (`ProfileNav`) — this menu only switches who is signed
 * in and which company is active, it doesn't hold settings any more.
 *
 * `variant="row"` renders it as the sidebar's bottom-pinned Profiel row
 * instead of the compact icon trigger — same menu, opening upward since the
 * trigger sits near the bottom of the viewport (`Sidebar`/`MobileDrawer`).
 * `variant="icon"` (default) is the standalone circular trigger used in
 * `ManageLayout`'s header, which has no sidebar to pin a row to.
 */
export function AccountMenu({ variant = 'icon' }: { variant?: 'icon' | 'row' }) {
  const { t } = useTranslation();
  const { me, permissions } = useMe();
  const { accounts, active, switchTo, addAccount, signOut, signOutAll } = useSession();
  const { company, companies, hasChoice, setActive } = useActiveCompany();

  const displayName =
    me.user?.name ?? me.user?.nickname ?? me.user?.email ?? t('accountMenu.trigger');
  const pendingCount = (me.pending_consents ?? []).length;
  const canManage = permissions.canManage;
  const otherAccounts = accounts.filter((account) => account.sub !== active?.sub);
  return (
    <Dropdown
      align={variant === 'row' ? 'start' : 'end'}
      placement={variant === 'row' ? 'top' : 'bottom'}
      stretch={variant === 'row'}
      label={t('accountMenu.trigger')}
      trigger={() =>
        variant === 'row' ? (
          <span className="text-ink hover:bg-mint-tint rounded-pill flex h-10 w-full items-center gap-2 px-3 text-sm">
            <span className="relative flex shrink-0 items-center justify-center">
              <UserIcon className="size-4" aria-hidden="true" />
              {pendingCount > 0 && (
                <span
                  aria-hidden="true"
                  className="bg-danger absolute -top-1.5 -right-1.5 flex size-3.5 items-center justify-center rounded-full text-[9px] leading-none font-semibold text-white"
                >
                  {pendingCount}
                </span>
              )}
            </span>
            {t('profile.nav.profile')}
          </span>
        ) : (
          <span className="border-ink bg-mint text-mint-ink rounded-pill relative flex size-14 shrink-0 items-center justify-center border">
            <UserIcon className="size-5" aria-hidden="true" />
            {pendingCount > 0 && (
              <span
                aria-hidden="true"
                className="bg-danger border-paper absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full border-2 text-[10px] leading-none font-semibold text-white"
              >
                {pendingCount}
              </span>
            )}
          </span>
        )
      }
    >
      <div className="w-72 max-w-[85vw] text-sm">
        {/* 1. Ingelogde gebruiker */}
        <Link
          to="/profile"
          role="menuitem"
          className="hover:bg-mint-tint -mx-3 flex items-center gap-3 rounded-2xl px-3 py-2"
        >
          <Avatar name={displayName} />
          <span className="min-w-0 flex-1">
            <span className="text-ink block truncate font-semibold">{displayName}</span>
            {me.user?.email && (
              <span className="text-accent block truncate text-xs">{me.user.email}</span>
            )}
          </span>
          <ChevronRight className="text-accent size-4 shrink-0" aria-hidden="true" />
        </Link>

        {/* 2. Andere accounts */}
        {otherAccounts.length > 0 && (
          <>
            <MenuDivider />
            <p className="text-accent px-2 pb-1.5 text-[11px] font-semibold tracking-wider uppercase">
              {t('accountMenu.accounts.label')}
            </p>
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {otherAccounts.map((account) => {
                const name = account.name ?? account.email ?? account.sub;
                return (
                  <li key={account.sub}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => switchTo(account.sub)}
                      className="hover:bg-mint-tint flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left"
                    >
                      <Avatar name={name} />
                      <span className="min-w-0 flex-1">
                        <span className="text-ink block truncate">{name}</span>
                        {account.email && account.name && (
                          <span className="text-accent block truncate text-xs">
                            {account.email}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        <MenuDivider />
        <button
          type="button"
          onClick={addAccount}
          className="hover:bg-mint-tint text-accent block w-full rounded-2xl px-2 py-2 text-left"
        >
          {t('accountMenu.accounts.addAnother')}
        </button>

        {/* 3. Bedrijf */}
        {hasChoice && (
          <>
            <MenuDivider />
            <p className="text-accent px-2 pb-1.5 text-[11px] font-semibold tracking-wider uppercase">
              {t('accountMenu.company.label')}
            </p>
            <ul className="m-0 flex list-none flex-col p-0">
              {companies.map((c) => {
                const isActive = company?.id === c.id;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={isActive}
                      onClick={() => setActive(c.id)}
                      className="hover:bg-mint-tint flex w-full items-center gap-2 rounded-2xl px-2 py-2 text-left"
                    >
                      <span className="w-4 shrink-0">
                        {isActive && <Check className="size-4" aria-hidden="true" />}
                      </span>
                      {c.name}
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {/* 4. Beheer */}
        {canManage && (
          <>
            <MenuDivider />
            <Link
              to="/manage"
              className="hover:bg-mint-tint flex items-center gap-2 rounded-2xl px-2 py-2"
            >
              <Settings2 className="size-4" aria-hidden="true" />
              {t('accountMenu.manage')}
            </Link>
          </>
        )}

        {/* 5. Uitloggen */}
        <MenuDivider />
        <button
          type="button"
          onClick={() => void signOut()}
          className="hover:bg-mint-tint block w-full rounded-2xl px-2 py-2 text-left"
        >
          {t('accountMenu.signOut')}
        </button>
        {accounts.length > 1 && (
          <button
            type="button"
            onClick={() => void signOutAll()}
            className="hover:bg-mint-tint text-danger block w-full rounded-2xl px-2 py-2 text-left"
          >
            {t('accountMenu.signOutAll')}
          </button>
        )}
      </div>
    </Dropdown>
  );
}
